'use client'

import { useRef } from 'react'
import { ArrowRight, Plane } from 'lucide-react'
import { ApplyButton } from '@/components/apply/ApplyButton'

const T = {
  en: {
    title: 'Boarding pass',
    passenger: 'Passenger',
    you: 'YOU',
    from: 'Lahore',
    to: 'Tokyo',
    flight: 'Flight',
    cls: 'Class',
    gate: 'Gate',
    seat: 'Seat',
    seatV: 'Open',
    boarding: 'Boarding',
    boardingV: '2026/27 intake',
    cta: 'Confirm your seat',
  },
  ja: {
    title: '搭乗券',
    passenger: '搭乗者',
    you: 'あなた',
    from: 'ラホール',
    to: '東京',
    flight: '便名',
    cls: 'クラス',
    gate: 'ゲート',
    seat: '座席',
    seatV: '空席あり',
    boarding: '搭乗',
    boardingV: '2026/27年度',
    cta: '座席を確保する',
  },
}

// deterministic "barcode"
const BARS = Array.from({ length: 46 }, (_, i) => ((i * 7919) % 13) % 4 + 1)

export function BoardingPass({ locale = 'en', className = '' }: { locale?: 'en' | 'ja'; className?: string }) {
  const t = T[locale]
  const ref = useRef<HTMLDivElement>(null)

  const onMove = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse' || !ref.current) return
    const r = ref.current.getBoundingClientRect()
    const x = (e.clientX - r.left) / r.width - 0.5
    const y = (e.clientY - r.top) / r.height - 0.5
    ref.current.style.transform = `perspective(900px) rotateY(${x * 10}deg) rotateX(${-y * 8}deg)`
  }
  const onLeave = () => {
    if (ref.current) ref.current.style.transform = ''
  }

  return (
    <div className={`ncp-float ${className}`}>
      <div
        ref={ref}
        onPointerMove={onMove}
        onPointerLeave={onLeave}
        data-spot
        className="relative overflow-hidden rounded-[1.75rem] bg-white shadow-[0_40px_80px_-30px_rgba(20,33,61,0.45)] ring-1 ring-brand-cool-gray/70 transition-transform duration-300 ease-out"
      >
        {/* header strip */}
        <div className="relative flex items-center justify-between overflow-hidden bg-brand-green px-5 py-3 text-white">
          <div className="ncp-asanoha absolute inset-0 opacity-80" aria-hidden />
          <span className="relative text-[11px] font-semibold uppercase tracking-[0.25em]">{t.title}</span>
          <span className="relative font-[family-name:var(--font-noto-serif-jp)] text-xs tracking-[0.2em] text-brand-gold">日本センター</span>
        </div>

        <div className="grid grid-cols-[1fr_auto]">
          <div className="p-5 sm:p-6">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-brand-muted">{t.passenger}</p>
            <p className="text-lg font-bold tracking-wide text-brand-navy">{t.you}</p>

            <div className="mt-4 flex items-center justify-between gap-3">
              <div>
                <p className="text-4xl font-bold leading-none tracking-tight text-brand-navy sm:text-5xl">LHE</p>
                <p className="mt-1 text-xs text-brand-muted">{t.from}</p>
              </div>
              <div className="relative flex flex-1 items-center">
                <span className="h-px flex-1 border-t-2 border-dashed border-brand-cool-gray" />
                <Plane className="ncp-pass-plane mx-1 h-5 w-5 rotate-45 text-brand-red" />
                <span className="h-px flex-1 border-t-2 border-dashed border-brand-cool-gray" />
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold leading-none tracking-tight text-brand-navy sm:text-5xl">NRT</p>
                <p className="mt-1 text-xs text-brand-muted">{t.to}</p>
              </div>
            </div>

            <dl className="mt-5 grid grid-cols-3 gap-x-3 gap-y-3 text-[10px] uppercase tracking-[0.14em] text-brand-muted">
              <div>
                <dt>{t.flight}</dt>
                <dd className="mt-0.5 text-sm font-bold normal-case tracking-normal text-brand-navy">NCP 2627</dd>
              </div>
              <div>
                <dt>{t.cls}</dt>
                <dd className="mt-0.5 text-sm font-bold normal-case tracking-normal text-brand-navy">N5 → N1</dd>
              </div>
              <div>
                <dt>{t.gate}</dt>
                <dd className="mt-0.5 text-sm font-bold normal-case tracking-normal text-brand-navy">Gulberg III</dd>
              </div>
              <div className="col-span-2">
                <dt>{t.boarding}</dt>
                <dd className="mt-0.5 text-sm font-bold normal-case tracking-normal text-brand-green">{t.boardingV}</dd>
              </div>
              <div>
                <dt>{t.seat}</dt>
                <dd className="mt-0.5 text-sm font-bold normal-case tracking-normal text-brand-red">{t.seatV}</dd>
              </div>
            </dl>
          </div>

          {/* stub */}
          <div className="relative flex w-16 flex-col items-center justify-between border-l-2 border-dashed border-brand-cool-gray bg-brand-soft-gray/60 py-5 sm:w-20">
            <span className="absolute -left-[11px] -top-[11px] h-5 w-5 rounded-full bg-[#f6f1e8] ring-1 ring-brand-cool-gray/70" aria-hidden />
            <span className="absolute -bottom-[11px] -left-[11px] h-5 w-5 rounded-full bg-[#f6f1e8] ring-1 ring-brand-cool-gray/70" aria-hidden />
            <span className="font-[family-name:var(--font-noto-serif-jp)] text-lg font-bold text-brand-red [writing-mode:vertical-rl]">日本行き</span>
            <div className="flex h-24 w-8 flex-col justify-between" aria-hidden>
              {BARS.slice(0, 22).map((w, i) => (
                <span key={i} className="block bg-brand-navy" style={{ height: w === 1 ? 1 : w === 2 ? 2 : 3, opacity: w === 4 ? 0 : 1 }} />
              ))}
            </div>
          </div>
        </div>

        <ApplyButton
          className="group flex items-center justify-between border-t border-brand-cool-gray/70 bg-white px-5 py-3.5 text-sm font-semibold text-brand-red transition-colors hover:bg-brand-red hover:text-white sm:px-6"
        >
          {t.cta}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </ApplyButton>
      </div>
    </div>
  )
}
